import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import createSafeContext from '@paradigm/utils/createSafeContext';
import { getUserId } from '@paradigm/store/src/auth/selectors';
import { logException } from '@paradigm/logging/src/logging';
import { usePreferencesStore } from '@paradigm/features/src/preferences/store';

import {
  clearAgentSession,
  getAgentUrl,
  setAgentUrl,
  getAgentToken,
  setAgentToken,
  getSelectedModel as loadSelectedModel,
  setSelectedModel as persistSelectedModel,
} from '#/api/terminal/agent-url';
import {
  fetchAvailableModels,
  getModelLabel,
  getStaticModels,
  isModelSwitchEcho,
} from '#/api/terminal/models-config';
import {
  clearChatHistory,
  deleteSessionMessages,
  generateSessionId,
  loadCustomFolders,
  loadHistoryUiState,
  loadSessionMessages,
  migrateLegacyHistoryIfNeeded,
  recoverOrphanedFolders,
  recoverOrphanedSessions,
  saveCustomFolders,
  saveHistoryUiState,
  saveSessionMessages,
  saveSessions,
} from '#/api/terminal/chat-history-storage';
import {
  AgentWebSocket,
  createAgentWebSocket,
} from '#/api/terminal/createAgentWebSocket';
import {
  AgentConnectionStatus,
  ApprovalRequest,
  ChatMessage,
  ChatSession,
  CustomFolder,
  GatewayFrame,
  ModelInfo,
  SessionFolder,
} from '#/api/terminal/types';

interface AgentState {
  readonly status: AgentConnectionStatus;
  readonly errorMessage: string | null;
  /** Messages for the active session — hydrated from per-session storage on switch. */
  readonly messages: readonly ChatMessage[];
  readonly agentUrl: string | null;
  /**
   * Ephemeral "the agent is doing something" indicator, driven off the
   * OpenClaw `agent` event's `data.state` field (and set optimistically on
   * `sendMessage`). Cleared the moment any concrete assistant output
   * arrives — delta, approval, error, or `*.done`.
   */
  readonly processing: {
    readonly label: string;
    readonly detail?: string;
  } | null;
  /** All known sessions for the current user, persisted across reloads. */
  readonly sessions: readonly ChatSession[];
  /** The session currently shown in the Chat tab. */
  readonly activeSessionId: string | null;
  /**
   * User-created folders that group sessions in Recents. Persisted per
   * user alongside `sessions`. A session belongs to a folder when its
   * `customFolderId` matches an entry here; otherwise it lives under the
   * default app folder.
   */
  readonly customFolders: readonly CustomFolder[];
  /**
   * Run ids the user explicitly cancelled via Stop. The gateway may not
   * implement `chat.cancel`, so any post-stop deltas / state events /
   * done frames are dropped client-side using this set instead of
   * being merged into the chat.
   */
  readonly cancelledRunIds: ReadonlySet<string>;
  /**
   * Models the picker exposes. Hydrated from the static per-cluster
   * table in `models-config.ts` once the agent URL is known. Empty
   * means the picker hides itself (unknown host).
   */
  readonly models: readonly ModelInfo[];
  /**
   * Currently selected model id. Persisted per-user so the pick
   * survives reloads and is re-sent to the gateway on reconnect
   * (OpenClaw's `/model` is per-session).
   */
  readonly selectedModelId: string | null;
}

interface AgentActions {
  readonly sendMessage: (content: string) => void;
  readonly connect: (url?: string) => void;
  readonly disconnect: () => void;
  /** Clear messages in the *active* session only — leaves other sessions intact. */
  readonly clearMessages: () => void;
  readonly respondToApproval: (approvalId: string, approved: boolean) => void;
  readonly request: <T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ) => Promise<T>;
  readonly requestPairing: () => Promise<string>;
  readonly setUrl: (url: string) => void;
  /**
   * Create a new session and mark it active. When `isTemporary` is set,
   * the session is kept in memory only — never persisted, never shown in
   * the Recents list, and dropped the moment the user creates another
   * chat or switches sessions.
   */
  readonly createSession: (opts: {
    readonly folder: SessionFolder;
    readonly isTemporary?: boolean;
  }) => string;
  /** Load `sessionId` into the Chat view. */
  readonly switchSession: (sessionId: string) => void;
  readonly renameSession: (sessionId: string, title: string) => void;
  readonly pinSession: (sessionId: string, pinned: boolean) => void;
  /** Remove a session and its persisted messages. Falls back to another session if the deleted one was active. */
  readonly deleteSession: (sessionId: string) => void;
  /**
   * Stop the in-flight prompt: clear the processing indicator, close any
   * streaming bubbles, and drop further frames from the cancelled run.
   * Best-effort tells the gateway via `chat.cancel`.
   */
  readonly stopProcessing: () => void;
  /**
   * Move a session into a user-created folder. Pass `undefined` to
   * remove the folder assignment (session goes back to its default
   * app folder).
   */
  readonly setSessionFolder: (
    sessionId: string,
    customFolderId: string | undefined,
  ) => void;
  /**
   * Create a user-named folder. Returns the new folder's id so callers
   * can immediately put it into rename / move-to flows.
   */
  readonly createCustomFolder: (name: string) => string;
  readonly renameCustomFolder: (folderId: string, name: string) => void;
  readonly pinCustomFolder: (folderId: string, pinned: boolean) => void;
  /**
   * Delete a custom folder. Any session currently assigned to it falls
   * back to its default app folder (no chats are lost).
   */
  readonly deleteCustomFolder: (folderId: string) => void;
  /**
   * Switch the active model via OpenClaw's `/model <id>` slash command.
   * Persists the pick per-user and re-sends it on reconnect.
   */
  readonly setSelectedModel: (modelId: string) => void;
}

const INITIAL_STATE: AgentState = {
  status: 'disconnected',
  errorMessage: null,
  messages: [],
  agentUrl: null,
  processing: null,
  sessions: [],
  activeSessionId: null,
  customFolders: [],
  cancelledRunIds: new Set<string>(),
  models: [],
  selectedModelId: null,
};

const DEFAULT_SESSION_TITLE = 'New Chat';
// Generous cap: the row's CSS ellipsis handles visual truncation per
// panel width, so we store enough characters for any reasonable size.
const SESSION_TITLE_MAX_LEN = 200;

function deriveSessionTitle(content: string): string {
  const oneLine = content.replace(/\s+/gu, ' ').trim();
  if (oneLine.length <= SESSION_TITLE_MAX_LEN) return oneLine;
  return `${oneLine.slice(0, SESSION_TITLE_MAX_LEN - 1)}…`;
}

const PROCESSING_STATE_LABELS: Readonly<Record<string, string>> = {
  thinking: 'Thinking',
  reasoning: 'Thinking',
  planning: 'Planning',
  running: 'Running',
  tool_use: 'Calling tool',
  tool_call: 'Calling tool',
  tool: 'Calling tool',
  searching: 'Searching',
  analyzing: 'Analyzing',
  streaming: 'Writing',
  writing: 'Writing',
};

const TERMINAL_PROCESSING_STATES: ReadonlySet<string> = new Set([
  'done',
  'complete',
  'completed',
  'finished',
  'idle',
  'ready',
]);

function humanizeState(state: string): string {
  return state
    .replace(/[_-]+/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
    .replace(/\b\w/gu, (c) => c.toUpperCase());
}

function resolveProcessingLabel(state: string): string {
  const key = state.toLowerCase();
  return PROCESSING_STATE_LABELS[key] ?? humanizeState(state);
}

function closeStreamingMessages(
  messages: readonly ChatMessage[],
): readonly ChatMessage[] {
  if (!messages.some((m) => m.isStreaming === true)) return messages;
  return messages.map((m) =>
    m.isStreaming === true ? { ...m, isStreaming: false } : m,
  );
}

/**
 * Pick which session should be active after hydration. Prefer the
 * persisted `activeSessionId` (still valid only if the session still
 * exists), otherwise the most-recently-updated session, otherwise null.
 */
function resolveActiveSession(
  sessions: readonly ChatSession[],
  persistedActiveId: string | null,
): string | null {
  if (sessions.length === 0) return null;
  if (
    persistedActiveId != null &&
    sessions.some((s) => s.sessionId === persistedActiveId)
  ) {
    return persistedActiveId;
  }
  const mostRecent = [...sessions].sort((a, b) => b.updatedAt - a.updatedAt)[0];
  return mostRecent?.sessionId ?? null;
}

export const [useAgentState, AgentStateProvider] =
  createSafeContext<AgentState>('AgentState');

export const [useAgentActions, AgentActionsProvider] =
  createSafeContext<AgentActions>('AgentActions');

interface Props {
  readonly children: ReactNode;
  /**
   * Static framing prepended to every outbound `chat.send` payload but NOT
   * shown in the chat bubble. Use it to tell the agent what context it's
   * operating in (e.g. "You are analyzing paradigm options block trades")
   * so a generic OpenClaw agent doesn't fall back to its default training.
   */
  readonly systemPreamble?: string;
}

let nextMsgId = Date.now();

export function AgentProvider({ children, systemPreamble }: Props) {
  const userId = useSelector(getUserId);
  // The auth reducer resets `user.id` to `0` on `logoutSuccess` instead
  // of `null`, so treat falsy / "empty user" as logged-out. Otherwise
  // we'd hold the agent session open for a phantom user `0` and never
  // run the cleanup branch below.
  const userIdStr =
    userId != null && userId !== 0 && String(userId).length > 0
      ? String(userId)
      : null;
  const userIdRef = useRef<string | null>(userIdStr);
  userIdRef.current = userIdStr;

  // Server-synced URL and token are the source of truth across devices;
  // localStorage is a fast read for the WebSocket layer below it.
  const persistedUrl = usePreferencesStore((s) => s.aiAgentUrl);
  const setPersistedUrl = usePreferencesStore((s) => s.setAiAgentUrl);
  const setPersistedUrlRef = useRef(setPersistedUrl);
  setPersistedUrlRef.current = setPersistedUrl;

  const persistedToken = usePreferencesStore((s) => s.aiAgentToken);
  const setPersistedToken = usePreferencesStore((s) => s.setAiAgentToken);
  const setPersistedTokenRef = useRef(setPersistedToken);
  setPersistedTokenRef.current = setPersistedToken;
  // Mirror into a ref so `connect` (memoized with empty deps to keep it
  // a stable callback) can read the latest value without listing
  // `persistedToken` as a dependency — adding it would force `connect`
  // to re-create on every token sync and re-fire the auth-hydration
  // effect, reconnecting the WS every 5s.
  const persistedTokenRef = useRef(persistedToken);
  persistedTokenRef.current = persistedToken;

  const systemPreambleRef = useRef(systemPreamble);
  systemPreambleRef.current = systemPreamble;

  const [state, setState] = useState<AgentState>(INITIAL_STATE);
  const wsRef = useRef<AgentWebSocket | null>(null);
  // Cancellation handle for the in-flight `/api/models` fetch — aborted
  // every time `connect` runs (new auth = stale catalog) and on unmount,
  // so rapid reconnects can't have a late older response setState after
  // a newer fetch has already landed.
  const modelsFetchAbortRef = useRef<AbortController | null>(null);
  // Tracks the user we've already triggered a post-auth history fetch for
  // on the current WS instance, so we don't refetch on every `authenticated`
  // re-emit (e.g. after a heartbeat reconnect that lands us back here).
  const historyFetchedForRef = useRef<string | null>(null);

  // Per-run session ownership: runId → sessionId. We learn this from the
  // gateway echoing back `sessionKey` on response frames; once known, late
  // frames for that run get routed to the *original* session even after
  // the user has switched away.
  const runOwnershipRef = useRef<Map<string, string>>(new Map());

  // In-memory message buffer for sessions that are receiving frames while
  // not active. Preserves `isStreaming` flags so when the user switches
  // back, the message can continue streaming instead of fragmenting into
  // a fresh bubble. Flushed to localStorage when the run wraps up.
  const backgroundSessionsRef = useRef<Map<string, readonly ChatMessage[]>>(
    new Map(),
  );

  // Synchronous mirror of state.activeSessionId so frame handlers can
  // decide routing without waiting for a re-render.
  const activeSessionIdRef = useRef<string | null>(null);
  activeSessionIdRef.current = state.activeSessionId;

  // Synchronous mirror of cancelledRunIds for fast no-state-read drop
  // checks at the top of handlers that no longer have access to `prev`.
  const cancelledRunIdsRef = useRef<ReadonlySet<string>>(new Set<string>());
  cancelledRunIdsRef.current = state.cancelledRunIds;

  // Set of session IDs we (the UI) created. The gateway echoes back its
  // own `mainSessionKey` on frames which is unrelated to our chat IDs —
  // guard against treating that as a target for routing.
  const knownSessionIdsRef = useRef<Set<string>>(new Set());
  knownSessionIdsRef.current = new Set(state.sessions.map((s) => s.sessionId));

  // Last session that issued a send. Used as a fallback to associate the
  // first incoming frame's runId with the session that originated it,
  // since the gateway may not echo our per-chat sessionKey reliably.
  const lastSendSessionRef = useRef<string | null>(null);

  // Run ids whose first delta was the gateway's `/model <id>` echo — the
  // picker already added an optimistic acknowledgment client-side, so
  // every subsequent delta for the same run is dropped (the echo is a
  // streaming message, not a single frame).
  const suppressedModelEchoRunIdsRef = useRef<Set<string>>(new Set());

  // Synchronous mirrors so the connect/auth handler can read the latest
  // pick + status without re-creating the WS on every change.
  const selectedModelIdRef = useRef<string | null>(null);
  selectedModelIdRef.current = state.selectedModelId;
  const statusRef = useRef<AgentConnectionStatus>('disconnected');
  statusRef.current = state.status;

  // Set by `clearMessages` to the just-cleared session id so the next
  // run of the messages-persistence effect skips the otherwise-empty
  // write that would recreate the localStorage key we just removed.
  // Consumed (cleared) inside the effect itself.
  const justClearedSessionRef = useRef<string | null>(null);

  /**
   * Resolve which session a frame belongs to and apply a message reducer
   * either to live state (active session) or to a background buffer
   * (any session the user has switched away from while its run is still
   * producing frames). Without this routing, the next click on "New chat"
   * mid-stream would let the in-flight response continue rendering into
   * the new chat instead of finishing in the original.
   */
  const routeMessageFrame = useCallback(
    (
      payload: { sessionKey?: string; runId?: string },
      reducer: (messages: readonly ChatMessage[]) => readonly ChatMessage[],
      activeExtra?: {
        processing?: { label: string; detail?: string } | null;
      },
    ): void => {
      // Only honor `sessionKey` from the payload if it actually names one
      // of our chat sessions — the gateway also echoes its own internal
      // mainSessionKey, which we must not mistake for a chat ID.
      const ourSessionKey =
        payload.sessionKey != null &&
        knownSessionIdsRef.current.has(payload.sessionKey)
          ? payload.sessionKey
          : null;
      if (ourSessionKey != null && payload.runId != null) {
        runOwnershipRef.current.set(payload.runId, ourSessionKey);
      } else if (
        payload.runId != null &&
        !runOwnershipRef.current.has(payload.runId) &&
        lastSendSessionRef.current != null &&
        knownSessionIdsRef.current.has(lastSendSessionRef.current)
      ) {
        // First frame for a runId we've never seen — associate it with
        // the most recent sender so subsequent frames stay in that chat.
        runOwnershipRef.current.set(payload.runId, lastSendSessionRef.current);
      }
      const targetId =
        ourSessionKey ??
        (payload.runId != null
          ? runOwnershipRef.current.get(payload.runId)
          : undefined) ??
        null;
      const activeId = activeSessionIdRef.current;

      if (targetId == null || targetId === activeId) {
        setState((prev) => {
          const next = reducer(prev.messages);
          if (
            next === prev.messages &&
            (activeExtra == null || !('processing' in activeExtra))
          ) {
            return prev;
          }
          return {
            ...prev,
            messages: next,
            ...(activeExtra != null && 'processing' in activeExtra
              ? { processing: activeExtra.processing ?? null }
              : {}),
          };
        });
        return;
      }

      const id = userIdRef.current;
      if (id == null) return;

      const existing =
        backgroundSessionsRef.current.get(targetId) ??
        loadSessionMessages(id, targetId);
      const next = reducer(existing);
      if (next === existing) return;

      backgroundSessionsRef.current.set(targetId, next);
      setState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.sessionId === targetId ? { ...s, updatedAt: Date.now() } : s,
        ),
      }));

      const stillStreaming = next.some((m) => m.isStreaming === true);
      if (!stillStreaming) {
        saveSessionMessages(id, targetId, next);
        backgroundSessionsRef.current.delete(targetId);
      }
    },
    [],
  );

  const connect = useCallback((url?: string) => {
    const id = userIdRef.current;
    if (id == null) return;
    const agentUrl = url ?? getAgentUrl(id);
    if (agentUrl == null || agentUrl.length === 0) return;

    if (wsRef.current != null) {
      wsRef.current.close();
    }
    // Abort any in-flight models fetch tied to the previous connection
    // so its setState can't race against the catalog we'll fetch under
    // this new socket.
    modelsFetchAbortRef.current?.abort();

    setState((prev) => ({
      ...prev,
      agentUrl,
      status: 'connecting',
    }));

    const ws = createAgentWebSocket(
      agentUrl,
      id,
      persistedTokenRef.current ?? undefined,
    );
    wsRef.current = ws;

    ws.onStatusChange((newStatus, errorMsg) => {
      setState((prev) => ({
        ...prev,
        status: newStatus,
        errorMessage:
          errorMsg ?? (newStatus === 'error' ? prev.errorMessage : null),
      }));

      // Reset the history-fetch guard whenever we leave 'authenticated', so
      // the next successful auth re-fetches.
      if (newStatus !== 'authenticated') {
        historyFetchedForRef.current = null;
      }

      if (
        newStatus === 'authenticated' &&
        historyFetchedForRef.current !== id
      ) {
        historyFetchedForRef.current = id;
        // Fetch recent chat history so reconnects show prior messages.
        // OpenClaw's `chat.history` is a tail-reader — it accepts `limit`
        // (hard max 1000) and ignores `before:`/`since:` cursor params, so
        // we always ask for the gateway's full window in one shot and
        // dedupe against locally-persisted messages on merge.
        fetchChatHistory(ws, setState);
      }

      // Re-sync the user's model pick to the gateway. OpenClaw's
      // `/model <id>` is per-session, so without this every reconnect
      // would silently revert to the configured default. The echo gets
      // dropped by `handleFrame` via the suppressed-runs set below.
      if (newStatus === 'authenticated') {
        const persistedModelId = selectedModelIdRef.current;
        if (persistedModelId != null) {
          try {
            ws.send(`/model ${persistedModelId}`);
          } catch (err) {
            logException('terminal:re-send model on reconnect', err);
          }
        }

        // Refresh the live model catalog from the terminal pod's shim.
        // The static table seeded at hydration keeps the picker usable
        // on first paint and on shim outage; this overlays the live
        // catalog so a `BEDROCK_MODEL_IDS` change on the gateway shows
        // up without a UI redeploy. We deliberately don't touch
        // `selectedModelId` — the `/model <id>` resync above has
        // already fired with the persisted pick; mutating selection now
        // would race that send. If the persisted id isn't in the live
        // list, ModelPicker falls back to `models[0]` cosmetically.
        //
        // Threaded with an AbortController so a rapid reconnect (e.g.
        // network blip) can't have a stale older catalog setState over
        // the fresh socket's. The controller is reset at the top of
        // `connect`; double-check `aborted` before commit since the
        // signal only aborts the fetch itself, not the .then callback.
        const modelsAbort = new AbortController();
        modelsFetchAbortRef.current = modelsAbort;
        fetchAvailableModels(agentUrl, { signal: modelsAbort.signal })
          .then((liveModels) => {
            if (modelsAbort.signal.aborted) return;
            if (liveModels.length === 0) return;
            setState((prev) => ({ ...prev, models: liveModels }));
          })
          .catch((err: unknown) => {
            logException('terminal:fetch models', err);
          });
      }
    });

    ws.onMessage((frame: GatewayFrame) => {
      handleFrame(frame, setState, {
        routing: {
          routeMessageFrame,
          isCancelled: (runId) =>
            runId != null && cancelledRunIdsRef.current.has(runId),
          forgetRun: (runId) => {
            if (runId != null) {
              runOwnershipRef.current.delete(runId);
              suppressedModelEchoRunIdsRef.current.delete(runId);
            }
          },
        },
        suppressedModelEchoRunIds: suppressedModelEchoRunIdsRef.current,
      });
    });
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current != null) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setState(() => ({ ...INITIAL_STATE }));
  }, []);

  const sendMessage = useCallback((content: string) => {
    if (wsRef.current == null) return;

    const userMsg: ChatMessage = {
      id: `user-${nextMsgId++}`,
      role: 'user',
      content,
      timestamp: Date.now(),
    };

    // Resolve the active session synchronously *before* setState so the
    // value we hand to `ws.send` below comes from a single source of
    // truth (not a side-effect inside the updater). React calls
    // updaters twice in StrictMode — relying on an outer-scoped
    // assignment from inside the updater works today only because both
    // runs produce the same value; an extra `generateSessionId()` per
    // call would otherwise be silently invisible.
    const now = Date.now();
    const resolvedSessionId = activeSessionIdRef.current ?? generateSessionId();
    const isNewSession = activeSessionIdRef.current == null;
    const newSession: ChatSession | null = isNewSession
      ? {
          sessionId: resolvedSessionId,
          folder: 'paradigm',
          title: DEFAULT_SESSION_TITLE,
          pinned: false,
          createdAt: now,
          updatedAt: now,
        }
      : null;

    setState((prev) => {
      const sessionsWithNew =
        newSession != null ? [newSession, ...prev.sessions] : prev.sessions;
      const sessions = sessionsWithNew.map((s) =>
        s.sessionId === resolvedSessionId
          ? {
              ...s,
              title:
                s.title === DEFAULT_SESSION_TITLE
                  ? deriveSessionTitle(content)
                  : s.title,
              updatedAt: now,
            }
          : s,
      );

      return {
        ...prev,
        sessions,
        activeSessionId: resolvedSessionId,
        processing: { label: 'Thinking' },
        messages: [...prev.messages, userMsg],
      };
    });

    // Prepend the system preamble (if any) to the gateway payload but
    // keep the chat bubble showing only what the user typed.
    const preamble = systemPreambleRef.current;
    const payload =
      preamble != null && preamble.length > 0
        ? `${preamble}\n\n${content}`
        : content;
    // Remember which session originated this send so the first frame
    // back (which may not echo `sessionKey`) routes to the right chat.
    lastSendSessionRef.current = resolvedSessionId;
    wsRef.current.send(payload, {
      sessionKey: resolvedSessionId,
    });
  }, []);

  const clearMessages = useCallback(() => {
    const userId = userIdRef.current;
    // Read the active session id off `activeSessionIdRef` (synchronous
    // mirror of state) *before* setState so the localStorage write
    // doesn't sit inside the updater. React calls updaters twice under
    // StrictMode; running the delete from inside one would fire it
    // twice and double-write the storage layer.
    const activeId = activeSessionIdRef.current;
    if (userId != null && activeId != null) {
      // Mark this session as just-cleared so the persistence effect
      // doesn't immediately recreate an empty blob under the same key.
      justClearedSessionRef.current = activeId;
      deleteSessionMessages(userId, activeId);
    }
    setState((prev) => ({ ...prev, messages: [], processing: null }));
  }, []);

  const createSession = useCallback(
    ({
      folder,
      isTemporary,
    }: {
      readonly folder: SessionFolder;
      readonly isTemporary?: boolean;
    }): string => {
      const id = generateSessionId();
      const now = Date.now();
      const newSession: ChatSession = {
        sessionId: id,
        folder,
        title: DEFAULT_SESSION_TITLE,
        pinned: false,
        createdAt: now,
        updatedAt: now,
        ...(isTemporary === true ? { isTemporary: true } : {}),
      };
      setState((prev) => {
        // Stash any in-flight streaming messages from the outgoing session
        // so subsequent frames for that run continue to land in the right
        // chat, and switching back later picks up the continued stream.
        if (
          prev.activeSessionId != null &&
          prev.messages.some((m) => m.isStreaming === true)
        ) {
          backgroundSessionsRef.current.set(prev.activeSessionId, [
            ...prev.messages,
          ]);
        }
        return {
          ...prev,
          // Drop any in-flight temporary session: creating a new chat
          // (temp or not) ends the previous temp chat's lifetime.
          sessions: [
            newSession,
            ...prev.sessions.filter((s) => s.isTemporary !== true),
          ],
          activeSessionId: id,
          messages: [],
          processing: null,
        };
      });
      return id;
    },
    [],
  );

  const switchSession = useCallback((sessionId: string) => {
    const id = userIdRef.current;
    if (id == null) return;
    // Prefer the in-memory snapshot if a background run has been writing
    // to this session — it preserves `isStreaming` flags that the
    // localStorage path strips on every save.
    const cached = backgroundSessionsRef.current.get(sessionId);
    const messages =
      cached != null ? [...cached] : loadSessionMessages(id, sessionId);
    if (cached != null) {
      backgroundSessionsRef.current.delete(sessionId);
    }
    setState((prev) => {
      if (!prev.sessions.some((s) => s.sessionId === sessionId)) return prev;
      // Stash the outgoing session's streaming-in-progress messages so
      // its run continues to update its own chat history, not the one
      // the user just switched to.
      if (
        prev.activeSessionId != null &&
        prev.activeSessionId !== sessionId &&
        prev.messages.some((m) => m.isStreaming === true)
      ) {
        backgroundSessionsRef.current.set(prev.activeSessionId, [
          ...prev.messages,
        ]);
      }
      return {
        ...prev,
        // Leaving a temporary chat ends its life — don't carry it
        // forward into the Recents list or anywhere else.
        sessions: prev.sessions.filter(
          (s) => s.isTemporary !== true || s.sessionId === sessionId,
        ),
        activeSessionId: sessionId,
        messages,
        processing: null,
      };
    });
  }, []);

  const renameSession = useCallback((sessionId: string, title: string) => {
    const trimmed = title.trim();
    if (trimmed.length === 0) return;
    setState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s) =>
        s.sessionId === sessionId
          ? {
              ...s,
              title: trimmed.slice(0, SESSION_TITLE_MAX_LEN),
            }
          : s,
      ),
    }));
  }, []);

  const pinSession = useCallback((sessionId: string, pinned: boolean) => {
    setState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s) =>
        s.sessionId === sessionId ? { ...s, pinned } : s,
      ),
    }));
  }, []);

  const deleteSession = useCallback((sessionId: string) => {
    const userId = userIdRef.current;
    if (userId != null) deleteSessionMessages(userId, sessionId);
    setState((prev) => {
      const remaining = prev.sessions.filter((s) => s.sessionId !== sessionId);
      if (prev.activeSessionId !== sessionId) {
        return { ...prev, sessions: remaining };
      }
      // The deleted session was active — fall back to the most-recent
      // remaining session, or create a fresh empty Paradex session if
      // nothing remains so the Chat tab isn't left in an awkward null
      // state.
      if (remaining.length === 0) {
        const now = Date.now();
        const fresh: ChatSession = {
          sessionId: generateSessionId(),
          folder: 'paradigm',
          title: DEFAULT_SESSION_TITLE,
          pinned: false,
          createdAt: now,
          updatedAt: now,
        };
        return {
          ...prev,
          sessions: [fresh],
          activeSessionId: fresh.sessionId,
          messages: [],
          processing: null,
        };
      }
      const next = [...remaining].sort((a, b) => b.updatedAt - a.updatedAt)[0]!;
      const nextMessages =
        userId != null ? loadSessionMessages(userId, next.sessionId) : [];
      return {
        ...prev,
        sessions: remaining,
        activeSessionId: next.sessionId,
        messages: nextMessages,
        processing: null,
      };
    });
  }, []);

  const setSessionFolder = useCallback(
    (sessionId: string, customFolderId: string | undefined) => {
      setState((prev) => ({
        ...prev,
        // Folder reassignment shouldn't bump the session's `updatedAt`
        // — that's reserved for actual chat activity (new messages).
        // Moving a chat into a folder should leave it sorted by its
        // original timestamp so the user sees the real conversation
        // recency, not the move time.
        sessions: prev.sessions.map((s) =>
          s.sessionId === sessionId
            ? customFolderId == null
              ? // Strip the field entirely when unassigning so the
                // persisted blob doesn't carry stale ids forever.
                (() => {
                  const { customFolderId: _drop, ...rest } = s;
                  return rest as ChatSession;
                })()
              : { ...s, customFolderId }
            : s,
        ),
      }));
    },
    [],
  );

  const createCustomFolder = useCallback((name: string): string => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return '';
    const folder: CustomFolder = {
      id: generateSessionId(),
      name: trimmed,
      createdAt: Date.now(),
    };
    setState((prev) => ({
      ...prev,
      customFolders: [...prev.customFolders, folder],
    }));
    return folder.id;
  }, []);

  const renameCustomFolder = useCallback((folderId: string, name: string) => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    setState((prev) => ({
      ...prev,
      customFolders: prev.customFolders.map((f) =>
        f.id === folderId ? { ...f, name: trimmed } : f,
      ),
    }));
  }, []);

  const pinCustomFolder = useCallback((folderId: string, pinned: boolean) => {
    setState((prev) => ({
      ...prev,
      customFolders: prev.customFolders.map((f) => {
        if (f.id !== folderId) return f;
        if (pinned) return { ...f, pinned: true };
        const { pinned: _drop, ...rest } = f;
        return rest as CustomFolder;
      }),
    }));
  }, []);

  const deleteCustomFolder = useCallback((folderId: string) => {
    setState((prev) => ({
      ...prev,
      customFolders: prev.customFolders.filter((f) => f.id !== folderId),
      // Detach sessions from the deleted folder so they don't disappear
      // from view — they fall back to the default app folder.
      sessions: prev.sessions.map((s) => {
        if (s.customFolderId !== folderId) return s;
        const { customFolderId: _drop, ...rest } = s;
        return rest as ChatSession;
      }),
    }));
  }, []);

  const respondToApproval = useCallback(
    (approvalId: string, approved: boolean) => {
      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          msg.approval?.approvalId === approvalId
            ? {
                ...msg,
                approval: {
                  ...msg.approval,
                  state: approved ? ('approved' as const) : ('denied' as const),
                },
              }
            : msg,
        ),
      }));
      wsRef.current
        ?.request('approval.respond', { approvalId, approved })
        .catch((err: unknown) => logException('terminal:approval', err));
    },
    [],
  );

  const request = useCallback(
    async <T,>(
      method: string,
      params?: Record<string, unknown>,
    ): Promise<T> => {
      if (wsRef.current == null) {
        throw new Error('Not connected');
      }
      return wsRef.current.request<T>(method, params);
    },
    [],
  );

  const requestPairing = useCallback(async () => {
    if (wsRef.current == null) {
      throw new Error('Not connected');
    }
    return wsRef.current.requestPairing();
  }, []);

  const setUrl = useCallback((url: string) => {
    const id = userIdRef.current;
    if (id == null) return;
    // localStorage cache is keyed by user — keeps reads in `createAgentWebSocket`
    // / `agent-url.ts` fast and avoids a redux roundtrip on every reconnect.
    // The preferences slice is the durable, cross-device source of truth.
    setAgentUrl(id, url);
    setPersistedUrlRef.current(url);
    setState((prev) => ({ ...prev, agentUrl: url }));
  }, []);

  const stopProcessing = useCallback(() => {
    // Issue the gateway cancel out-of-band so StrictMode's double-run
    // of the setState updater can't fire two `chat.cancel` requests.
    // We snapshot the in-flight runIds off the synchronous mirrors
    // (state.messages via `cancelledRunIdsRef` isn't quite right — we
    // need the *current* streaming runs) so we know what to cancel
    // before mutating state.
    const messagesSnapshot = state.messages;
    const hasStreamingNow = messagesSnapshot.some(
      (m) => m.isStreaming === true,
    );
    if (state.processing == null && !hasStreamingNow) return;

    const lastStreaming = [...messagesSnapshot]
      .reverse()
      .find((m) => m.isStreaming === true);
    const cancelRunId = lastStreaming?.runId;
    if (wsRef.current != null) {
      wsRef.current
        .request(
          'chat.cancel',
          cancelRunId != null ? { runId: cancelRunId } : undefined,
        )
        .catch(() => {
          // Gateway doesn't implement chat.cancel — fine.
        });
    }

    setState((prev) => {
      const hasStreaming = prev.messages.some((m) => m.isStreaming === true);
      if (prev.processing == null && !hasStreaming) return prev;

      // Capture every still-streaming run id so handleFrame drops any
      // post-stop deltas / state events from those runs instead of
      // resurrecting a streaming bubble.
      const cancelledIds = new Set(prev.cancelledRunIds);
      for (const m of prev.messages) {
        if (m.isStreaming === true && m.runId != null) {
          cancelledIds.add(m.runId);
        }
      }

      // Visible acknowledgement so the user knows Stop took effect.
      const stoppedMsg: ChatMessage = {
        id: `stopped-${nextMsgId++}`,
        role: 'assistant' as const,
        content: '_Stopped._',
        timestamp: Date.now(),
      };

      return {
        ...prev,
        processing: null,
        messages: [...closeStreamingMessages(prev.messages), stoppedMsg],
        cancelledRunIds: cancelledIds,
      };
    });
  }, [state.messages, state.processing]);

  const setSelectedModel = useCallback((modelId: string) => {
    const userId = userIdRef.current;
    const label = getModelLabel(modelId);
    const isAuthenticated = statusRef.current === 'authenticated';

    // Always update UI state + persist so the pick survives reload and
    // is re-sent to the gateway on next authentication. Only add the
    // optimistic ack when authenticated — otherwise the `/model`
    // dispatch is a no-op and the ack would be a lie.
    setState((prev) => ({
      ...prev,
      selectedModelId: modelId,
      messages: isAuthenticated
        ? [
            ...prev.messages,
            {
              id: `model-pick-${nextMsgId++}`,
              role: 'assistant' as const,
              content: `Model set to ${label} for this session.`,
              timestamp: Date.now(),
            },
          ]
        : prev.messages,
    }));
    if (userId != null) persistSelectedModel(userId, modelId);

    // Dispatch OpenClaw's built-in `/model <id>` slash command via the
    // raw WS path so it doesn't appear as a user message in chat history.
    // The gateway's echo gets dropped via `suppressedModelEchoRunIdsRef`.
    if (isAuthenticated && wsRef.current != null) {
      try {
        wsRef.current.send(`/model ${modelId}`, {
          sessionKey: activeSessionIdRef.current ?? undefined,
        });
      } catch (err) {
        logException('terminal:set selected model', err);
      }
    }
  }, []);

  const actions = useMemo(
    () => ({
      sendMessage,
      connect,
      disconnect,
      clearMessages,
      respondToApproval,
      request,
      requestPairing,
      setUrl,
      createSession,
      switchSession,
      renameSession,
      pinSession,
      deleteSession,
      stopProcessing,
      setSessionFolder,
      createCustomFolder,
      renameCustomFolder,
      pinCustomFolder,
      deleteCustomFolder,
      setSelectedModel,
    }),
    [
      sendMessage,
      connect,
      disconnect,
      clearMessages,
      respondToApproval,
      request,
      requestPairing,
      setUrl,
      createSession,
      switchSession,
      renameSession,
      pinSession,
      deleteSession,
      stopProcessing,
      setSessionFolder,
      createCustomFolder,
      renameCustomFolder,
      pinCustomFolder,
      deleteCustomFolder,
      setSelectedModel,
    ],
  );

  // Auto-connect when a user id and stored URL are both available, and
  // tear down on sign-out / user switch.
  const hydratedUserRef = useRef<string | null>(null);
  const prevUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (userIdStr == null) {
      if (wsRef.current != null) {
        wsRef.current.close();
        wsRef.current = null;
      }
      // Tokens and chat history are per-user secrets; wipe them on
      // sign-out so a second user on the same browser doesn't inherit
      // them. The DB-synced preferences blob is the cross-device source
      // of truth and will re-seed both on the next login.
      const prev = prevUserIdRef.current;
      if (prev != null) {
        clearAgentSession(prev);
        clearChatHistory(prev);
      }
      prevUserIdRef.current = null;
      hydratedUserRef.current = null;
      setState({ ...INITIAL_STATE });
      return;
    }

    prevUserIdRef.current = userIdStr;

    if (hydratedUserRef.current === userIdStr) return;

    // DB preferences are authoritative across devices; localStorage is
    // only a per-device cache. If the two disagree, the DB wins — a stale
    // (or invalid testing-artifact) cached URL otherwise keeps the WS
    // pinned to an unreachable host and contributes to the connection
    // storm on side-panel reconnects.
    const localUrl = getAgentUrl(userIdStr);
    const hasPersisted = persistedUrl != null && persistedUrl.length > 0;
    const effectiveUrl =
      hasPersisted && persistedUrl != null && persistedUrl.length > 0
        ? persistedUrl
        : localUrl;
    if (effectiveUrl == null || effectiveUrl.length === 0) {
      if (wsRef.current != null) {
        wsRef.current.close();
        wsRef.current = null;
      }
      // Reset the hydration guard so when a URL eventually shows up
      // (preferences sync, manual entry) we re-hydrate sessions instead
      // of clobbering the empty-state we just wrote.
      hydratedUserRef.current = null;
      setState({ ...INITIAL_STATE });
      return;
    }

    if (wsRef.current != null) {
      wsRef.current.close();
      wsRef.current = null;
    }
    hydratedUserRef.current = userIdStr;

    // Sync localStorage to the DB value when they diverge. Stale tokens
    // are bound to the previous URL's gateway and would fail with
    // "gateway token mismatch", so clear them on a URL change.
    if (hasPersisted && persistedUrl !== localUrl) {
      setAgentUrl(userIdStr, effectiveUrl);
      if (localUrl != null && localUrl.length > 0) {
        clearAgentSession(userIdStr);
      }
    } else if (localUrl == null || localUrl.length === 0) {
      // Mirror the DB-only URL into localStorage so the WS / token
      // lookups (keyed off userId-scoped localStorage entries) work
      // without redux on subsequent reconnects.
      setAgentUrl(userIdStr, effectiveUrl);
    }

    // Similarly, sync the token from preferences to localStorage if available.
    // This allows pre-populated tokens (e.g., injected via SQL) to be used on
    // reconnects without hitting the preferences API.
    if (persistedToken != null && persistedToken.length > 0) {
      const localToken = getAgentToken(userIdStr);
      if (localToken == null || localToken.length === 0) {
        setAgentToken(userIdStr, persistedToken);
      }
    }

    // Hydrate sessions + the active session's messages from localStorage
    // so a refresh repaints the Recents list and the open chat instantly —
    // Telegram-style. The reconnect that follows will request anything
    // newer via `chat.history` (still routed into the active session for
    // v1; the gateway can't yet filter history by sessionKey).
    //
    // TODO(openclaw): once `chat.history` supports a `sessionKey` filter,
    // request per-session and merge into the matching session's blob
    // instead of unconditionally into the active session — otherwise
    // post-reconnect a user can see messages from a different chat
    // appear in the currently-open one. Track as a gateway follow-up.
    const migratedSessions = migrateLegacyHistoryIfNeeded(userIdStr);
    // Walk localStorage for per-session message blobs whose index entry
    // is missing — a regression once orphaned chat history blobs from
    // the sessions index, and this re-attaches them so users don't lose
    // chats.
    const recovered = recoverOrphanedSessions(userIdStr, migratedSessions);
    if (recovered.recovered) {
      saveSessions(
        userIdStr,
        recovered.sessions.filter((s) => s.isTemporary !== true),
      );
    }
    const hydratedSessions = recovered.sessions;
    // Custom folders are persisted under their own key; load them
    // alongside the sessions index and recover any folder ids referenced
    // by sessions but missing from the index.
    const existingFolders = loadCustomFolders(userIdStr);
    const recoveredFolders = recoverOrphanedFolders(
      hydratedSessions,
      existingFolders,
    );
    if (recoveredFolders.recovered) {
      saveCustomFolders(userIdStr, recoveredFolders.folders);
    }
    const uiState = loadHistoryUiState(userIdStr);
    const activeSessionId = resolveActiveSession(
      hydratedSessions,
      uiState.activeSessionId,
    );
    const persistedMessages =
      activeSessionId != null
        ? loadSessionMessages(userIdStr, activeSessionId)
        : [];
    const hydratedModels = getStaticModels(effectiveUrl);
    const persistedModelId = loadSelectedModel(userIdStr);
    const hydratedSelectedModelId =
      persistedModelId != null &&
      hydratedModels.some((m) => m.id === persistedModelId)
        ? persistedModelId
        : hydratedModels.find((m) => m.legacy !== true)?.id ??
          hydratedModels[0]?.id ??
          null;
    setState({
      ...INITIAL_STATE,
      agentUrl: effectiveUrl,
      sessions: hydratedSessions,
      activeSessionId,
      customFolders: recoveredFolders.folders,
      messages: persistedMessages,
      models: hydratedModels,
      selectedModelId: hydratedSelectedModelId,
    });

    // Connect even without a token — the gateway lets us open a pairing
    // role and call `node.pair.request` for self-service pairing.
    connect(effectiveUrl);
  }, [userIdStr, persistedUrl, persistedToken, connect]);

  // Persist the active session's messages so a reload has something to
  // show. Gated on `hydratedUserRef` matching the current user — the first
  // render after hydration would otherwise rewrite the same blob, and a
  // stale render under a freshly-switched user would clobber the new
  // user's transcript. Temporary chats are skipped: their whole contract
  // is "never written to disk".
  useEffect(() => {
    if (userIdStr == null) return;
    if (hydratedUserRef.current !== userIdStr) return;
    if (state.activeSessionId == null) return;
    const active = state.sessions.find(
      (s) => s.sessionId === state.activeSessionId,
    );
    if (active?.isTemporary === true) return;
    // `clearMessages` deleted the per-session blob just before this
    // effect re-ran with `messages: []`. Skip the write so we don't
    // immediately recreate an empty entry under the same key (which
    // would then sit in storage forever, unfreeable). Consume the
    // sentinel — subsequent saves (e.g. once new messages arrive) go
    // through normally.
    if (
      justClearedSessionRef.current === state.activeSessionId &&
      state.messages.length === 0
    ) {
      justClearedSessionRef.current = null;
      return;
    }
    justClearedSessionRef.current = null;
    saveSessionMessages(userIdStr, state.activeSessionId, state.messages);
  }, [userIdStr, state.activeSessionId, state.messages, state.sessions]);

  // Persist the sessions index whenever it changes. Temporary chats are
  // filtered out so they never leak into localStorage.
  useEffect(() => {
    if (userIdStr == null) return;
    if (hydratedUserRef.current !== userIdStr) return;
    saveSessions(
      userIdStr,
      state.sessions.filter((s) => s.isTemporary !== true),
    );
  }, [userIdStr, state.sessions]);

  // Persist the custom-folders index whenever it changes. Mirrors the
  // sessions-index persistence guard so a stale render under a freshly
  // switched user doesn't clobber the new user's folders.
  useEffect(() => {
    if (userIdStr == null) return;
    if (hydratedUserRef.current !== userIdStr) return;
    saveCustomFolders(userIdStr, state.customFolders);
  }, [userIdStr, state.customFolders]);

  // Persist the active session id so a reload re-opens the same chat.
  // Collapsed-folder state is owned by the Recents view and saved by it.
  const lastSavedActiveRef = useRef<string | null>(null);
  useEffect(() => {
    if (userIdStr == null) return;
    if (hydratedUserRef.current !== userIdStr) return;
    if (lastSavedActiveRef.current === state.activeSessionId) return;
    lastSavedActiveRef.current = state.activeSessionId;
    const ui = loadHistoryUiState(userIdStr);
    saveHistoryUiState(userIdStr, {
      ...ui,
      activeSessionId: state.activeSessionId,
    });
  }, [userIdStr, state.activeSessionId]);

  // Sync newly acquired tokens to preferences so they persist cross-device.
  // Check on a timer since tokens are set by the WebSocket handler and we
  // don't have direct visibility into when that happens.
  useEffect(() => {
    if (userIdStr == null) return;

    const checkAndSyncToken = () => {
      const localToken = getAgentToken(userIdStr);
      if (
        localToken != null &&
        localToken.length > 0 &&
        persistedToken !== localToken
      ) {
        setPersistedTokenRef.current(localToken);
      }
    };

    const interval = setInterval(checkAndSyncToken, 5000);
    return () => clearInterval(interval);
  }, [userIdStr, persistedToken]);

  useEffect(() => {
    return () => {
      wsRef.current?.close();
      modelsFetchAbortRef.current?.abort();
    };
  }, []);

  return (
    <AgentStateProvider value={state}>
      <AgentActionsProvider value={actions}>{children}</AgentActionsProvider>
    </AgentStateProvider>
  );
}

interface FrameRouting {
  readonly routeMessageFrame: (
    payload: { sessionKey?: string; runId?: string },
    reducer: (messages: readonly ChatMessage[]) => readonly ChatMessage[],
    activeExtra?: {
      processing?: { label: string; detail?: string } | null;
    },
  ) => void;
  readonly isCancelled: (runId: string | undefined) => boolean;
  /**
   * Drop the runId → sessionId entry once a run terminates. Without
   * pruning, `runOwnershipRef` would grow unbounded for the lifetime
   * of the page — every run ever observed stays in the map forever.
   * Called on `agent.done` / `chat.done` / `data.state === 'error'`.
   */
  readonly forgetRun: (runId: string | undefined) => void;
}

interface HandleFrameOptions {
  /**
   * Per-session routing for message-producing frames. When omitted (e.g.
   * in unit tests), all frames apply to whatever state `setState` exposes
   * — preserving the pre-routing single-session behaviour. The provider
   * wires this in for real so an in-flight run keeps writing to its
   * original session even after the user clicks `+ New chat`.
   */
  readonly routing?: FrameRouting;
  /**
   * Run ids whose first delta was detected as the gateway's `/model <id>`
   * echo. The streaming-delta handler drops every subsequent delta with
   * the same `runId` so the optimistic client-side ack added in
   * `setSelectedModel` isn't duplicated.
   */
  readonly suppressedModelEchoRunIds?: Set<string>;
}

export function handleFrame(
  frame: GatewayFrame,
  setState: Dispatch<SetStateAction<AgentState>>,
  options: HandleFrameOptions = {},
) {
  // Fallbacks let `handleFrame` keep working without per-session routing
  // (unit tests, etc.) — in that mode every frame applies to current state.
  const routeMessageFrame: FrameRouting['routeMessageFrame'] =
    options.routing?.routeMessageFrame ??
    ((_payload, reducer, activeExtra) => {
      setState((prev) => {
        const next = reducer(prev.messages);
        if (
          next === prev.messages &&
          (activeExtra == null || !('processing' in activeExtra))
        ) {
          return prev;
        }
        return {
          ...prev,
          messages: next,
          ...(activeExtra != null && 'processing' in activeExtra
            ? { processing: activeExtra.processing ?? null }
            : {}),
        };
      });
    });
  const isCancelled: FrameRouting['isCancelled'] =
    options.routing?.isCancelled ?? (() => false);
  const forgetRun: FrameRouting['forgetRun'] =
    options.routing?.forgetRun ??
    (() => {
      // No-op when running without a real routing object (unit tests).
    });
  if (frame.type === 'event') {
    const evt = frame;

    if (evt.event === 'agent') {
      const payload = evt.payload as
        | {
            stream?: string;
            data?: {
              text?: string;
              delta?: string;
              state?: string;
              errorMessage?: string;
              tool?: string;
              toolName?: string;
            };
            runId?: string;
            sessionKey?: string;
          }
        | undefined;

      if (payload == null) return;

      const { data } = payload;
      if (data == null) return;

      if (data.state === 'error') {
        if (isCancelled(payload.runId)) {
          // Even cancelled runs should release their ownership entry
          // so the map doesn't keep their runId forever.
          forgetRun(payload.runId);
          return;
        }
        routeMessageFrame(
          payload,
          (messages) => [
            ...closeStreamingMessages(messages),
            {
              id: `error-${nextMsgId++}`,
              role: 'assistant' as const,
              content: `Error: ${data.errorMessage ?? 'Unknown error'}`,
              timestamp: Date.now(),
            },
          ],
          { processing: null },
        );
        forgetRun(payload.runId);
        return;
      }

      // Streaming assistant text — use delta to update the last message
      if (payload.stream === 'assistant' && data.delta != null) {
        const { runId } = payload;
        if (isCancelled(runId)) return;
        const suppressed = options.suppressedModelEchoRunIds;
        // If this run was already flagged as a `/model` echo, drop every
        // subsequent delta — the optimistic ack is already in chat.
        if (runId != null && suppressed?.has(runId) === true) {
          return;
        }
        // First delta of a never-seen-before run. If it looks like the
        // gateway's `/model <id>` echo, mark the run as suppressed so the
        // rest of the stream is dropped and don't render this frame.
        if (
          runId != null &&
          isModelSwitchEcho(data.delta) &&
          // Only treat as echo if there isn't already an in-flight
          // assistant bubble for this run (i.e. this really is the
          // first delta).
          suppressed != null
        ) {
          // Peek without mutating state — we only need to know if the
          // last message in the live (or background) buffer already
          // carries this runId. Since `routeMessageFrame` does the
          // routing, defer to its reducer and add to the set only if
          // the bubble would be new. To stay simple here, assume the
          // first echo delta arrives before any other delta for the
          // same runId — true for `/model` since it's a synchronous
          // single-frame reply.
          suppressed.add(runId);
          return;
        }
        routeMessageFrame(
          payload,
          (messages) => {
            const lastMsg = messages[messages.length - 1];
            if (
              lastMsg != null &&
              lastMsg.role === 'assistant' &&
              lastMsg.isStreaming === true &&
              lastMsg.runId === runId
            ) {
              const updated = {
                ...lastMsg,
                content: lastMsg.content + data.delta!,
              };
              return [...messages.slice(0, -1), updated];
            }
            return [
              ...closeStreamingMessages(messages),
              {
                id: `assistant-${nextMsgId++}`,
                role: 'assistant' as const,
                content: data.delta!,
                timestamp: Date.now(),
                isStreaming: true,
                runId,
              },
            ];
          },
          { processing: null },
        );
        return;
      }

      if (typeof data.state === 'string' && data.state.length > 0) {
        const stateKey = data.state.toLowerCase();
        if (TERMINAL_PROCESSING_STATES.has(stateKey)) {
          setState((prev) => ({
            ...prev,
            processing: null,
            messages: closeStreamingMessages(prev.messages),
          }));
          return;
        }
        const label = resolveProcessingLabel(data.state);
        const rawDetail = data.toolName ?? data.tool ?? data.text;
        const detail =
          rawDetail != null && rawDetail.length > 0 ? rawDetail : undefined;
        setState((prev) => {
          const lastMsg = prev.messages[prev.messages.length - 1];
          if (lastMsg?.isStreaming === true) return prev;
          return {
            ...prev,
            processing: { label, detail },
          };
        });
        return;
      }

      return;
    }

    if (evt.event === 'chat') {
      const payload = evt.payload as
        | {
            state?: string;
            text?: string;
            errorMessage?: string;
          }
        | undefined;

      if (payload?.state === 'error') {
        setState((prev) => ({
          ...prev,
          processing: null,
          messages: [
            ...closeStreamingMessages(prev.messages),
            {
              id: `error-${nextMsgId++}`,
              role: 'assistant' as const,
              content: `Error: ${payload.errorMessage ?? 'Unknown error'}`,
              timestamp: Date.now(),
            },
          ],
        }));
      }
      return;
    }

    if (
      evt.event === 'agent.approval' ||
      evt.event === 'approval.request' ||
      evt.event === 'tool.approval'
    ) {
      const payload = evt.payload as
        | { id?: string; command?: string; description?: string }
        | undefined;
      if (payload?.command != null) {
        const approval: ApprovalRequest = {
          approvalId: payload.id ?? `approval-${nextMsgId++}`,
          command: payload.command,
          description: payload.description,
          state: 'pending',
        };
        setState((prev) => ({
          ...prev,
          processing: null,
          messages: [
            ...closeStreamingMessages(prev.messages),
            {
              id: `approval-${nextMsgId++}`,
              role: 'assistant' as const,
              content: payload.description ?? `Approve: ${payload.command}`,
              timestamp: Date.now(),
              approval,
            },
          ],
        }));
      }
      return;
    }

    if (evt.event === 'agent.done' || evt.event === 'chat.done') {
      const payload = (evt.payload ?? {}) as {
        runId?: string;
        sessionKey?: string;
      };
      if (isCancelled(payload.runId)) {
        forgetRun(payload.runId);
        return;
      }
      routeMessageFrame(
        payload,
        (messages) => {
          const closed = closeStreamingMessages(messages);
          const lastMsg = closed[closed.length - 1];
          if (
            lastMsg != null &&
            lastMsg.role === 'assistant' &&
            lastMsg.approval == null
          ) {
            const approval = detectApprovalRequest(lastMsg.content);
            if (approval != null) {
              return [...closed.slice(0, -1), { ...lastMsg, approval }];
            }
          }
          return closed;
        },
        { processing: null },
      );
      forgetRun(payload.runId);
      return;
    }

    return;
  }

  // GatewayResponse — surface error responses as messages
  {
    const res = frame;
    if (!res.ok && res.error != null) {
      setState((prev) => ({
        ...prev,
        processing: null,
        messages: [
          ...closeStreamingMessages(prev.messages),
          {
            id: `error-${nextMsgId++}`,
            role: 'assistant' as const,
            content: `Error: ${res.error?.message ?? 'Request failed'}`,
            timestamp: Date.now(),
          },
        ],
      }));
    }
  }
}

/** Shape of a single message in the `chat.history` RPC response. */
interface HistoryMessage {
  readonly role?: string;
  readonly content?: string | ReadonlyArray<{ type?: string; text?: string }>;
  readonly timestamp?: number;
}

/**
 * Maximum number of messages OpenClaw's `chat.history` will return in one
 * call. Asking for more is silently clamped to this value upstream, and the
 * RPC has no cursor pagination — this is the deepest tail we can pull.
 */
const CHAT_HISTORY_GATEWAY_LIMIT = 1000;

/**
 * Fetch recent chat history from the gateway after authentication.
 *
 * In session mode (the user has selected or just created a session) we
 * skip merging: the gateway's `chat.history` is a flat tail that doesn't
 * correspond to any UI `sessionKey`, so merging it would either clobber a
 * user-created clean session (the bug where clicking "+ New chat"
 * resurrected the prior transcript) or stitch unrelated sessions
 * together. Local cache is the source of truth per session.
 */
function fetchChatHistory(
  ws: AgentWebSocket,
  setState: Dispatch<SetStateAction<AgentState>>,
): void {
  ws.request<{ messages?: readonly HistoryMessage[] }>('chat.history', {
    limit: CHAT_HISTORY_GATEWAY_LIMIT,
  })
    .then((res) => {
      const seeded = normalizeHistoryMessages(res.messages);
      if (seeded.length === 0) return;

      setState((prev) => {
        // Session mode: never pollute a user-managed session with the
        // gateway's flat tail.
        if (prev.activeSessionId != null) return prev;
        if (prev.messages.length === 0) {
          return { ...prev, messages: seeded };
        }
        const seen = new Set(prev.messages.map(messageDedupKey));
        const additions = seeded.filter((m) => !seen.has(messageDedupKey(m)));
        if (additions.length === 0) return prev;
        const merged = [...prev.messages, ...additions].sort(
          (a, b) => a.timestamp - b.timestamp,
        );
        return { ...prev, messages: merged };
      });
    })
    .catch(() => {
      // Gateway doesn't support chat.history — silently skip.
    });
}

function messageDedupKey(m: ChatMessage): string {
  return `${m.timestamp}|${m.role}|${m.content}`;
}

function normalizeHistoryMessages(
  raw: readonly HistoryMessage[] | undefined,
): ChatMessage[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((m: HistoryMessage, i: number) => {
    let text = '';
    if (typeof m.content === 'string') {
      text = m.content;
    } else if (Array.isArray(m.content)) {
      text = (m.content as ReadonlyArray<{ type?: string; text?: string }>)
        .filter((c) => c.type === 'text' && c.text != null)
        .map((c) => c.text!)
        .join('');
    }
    return {
      id: `history-${m.timestamp ?? 'x'}-${i}`,
      role: m.role === 'user' ? 'user' : 'assistant',
      content: text,
      timestamp: m.timestamp ?? Date.now(),
    };
  });
}

/**
 * Detect approval-request patterns in agent text messages, e.g.
 *   "please approve the `some command` command"
 *   "approval needed — please approve the `...` command"
 */
function detectApprovalRequest(text: string): ApprovalRequest | null {
  const approvePattern =
    /(?:please\s+)?approv(?:e|al\s+(?:needed|required))[^`]*`([^`]+)`/iu;
  const match = approvePattern.exec(text);
  if (match != null) {
    const desc = text.replace(approvePattern, '').trim();
    return {
      approvalId: `text-approval-${nextMsgId++}`,
      command: match[1] ?? '',
      description: desc.length > 0 ? desc : undefined,
      state: 'pending',
    };
  }
  return null;
}
